import React, { useRef, useEffect, useState } from "react";
import "./Contact.css";
import gsap from "gsap";

import ContactForm from "../../components/ContactForm/ContactForm";
import Footer from "../../components/Footer/Footer";

import ReactLenis from "lenis/react";

import Transition from "../../components/Transition/Transition";
import { handleOverlay } from "../../utils/overlayManager";
import { CDN_CONFIG } from "../../config/cdn";
import { setSeo } from "../../utils/seo";

const Contact = () => {
  const videoRef = useRef(null);
  const videoWrapperRef = useRef(null);
  const [videoError, setVideoError] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const [isMobile, setIsMobile] = useState(false);


  // SEO
  useEffect(() => {
    setSeo({
      title: "Contact VZBL | Start your next project",
      description:
        "Get in touch with VZBL for branding, marketing, advertising, and production. We reply to serious inquiries.",
      url: `${window.location.origin}/contact`,
      image: `${window.location.origin}/Favicon180.png`,
      type: 'website',
      keywords: ['contact VZBL', 'creative agency contact', 'branding inquiry'],
    });
  }, []);

  useEffect(() => {
    handleOverlay();
    return () => handleOverlay();
  }, []);

  // Detect mobile to choose correct source (like Home.jsx)
  useEffect(() => {
    const checkMobile = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
      setIsMobile(mobile);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Animate the video background (blur effect)
    if (videoWrapperRef.current) {
      gsap.to(videoWrapperRef.current, {
        filter: "blur(100px)",
        duration: 1,
        ease: "power2.inOut"
      });
    }
  }, []);

  // Video loading with mobile autoplay hardening
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    try {
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('muted', '');
      video.muted = true;
      video.defaultMuted = true;
      video.preload = 'auto';
    } catch {
      // no-op
    }

    const tryPlay = () => {
      const p = video.play?.();
      if (p && typeof p.then === 'function') {
        p.catch(() => {});
      }
    };

    const playWithRetry = (retries = 4, delay = 300) => {
      tryPlay();
      if (retries <= 0) return;
      setTimeout(() => playWithRetry(retries - 1, Math.min(delay + 200, 1200)), delay);
    };

    // Add error handler for video
    const handleVideoError = () => {
      setVideoError(true);
      // Trigger fade-in for fallback
      setTimeout(() => setFadeIn(true), 100);
    };
    
    // Handle successful loading
    const handleCanPlay = () => {
      setVideoLoaded(true);
      playWithRetry();
      // Video is ready, trigger fade-in effect
      setTimeout(() => setFadeIn(true), 100);
    };
    
    // Add event listeners
    video.addEventListener('error', handleVideoError);
    video.addEventListener('canplay', handleCanPlay);

    // Fallback: attempt on first gesture
    const resumeOnUserGesture = () => {
      playWithRetry(2, 200);
      window.removeEventListener('touchstart', resumeOnUserGesture);
      window.removeEventListener('click', resumeOnUserGesture);
    };
    window.addEventListener('touchstart', resumeOnUserGesture, { once: true });
    window.addEventListener('click', resumeOnUserGesture, { once: true });
    
    // Safety timeout for video loading
    const loadTimeout = setTimeout(() => {
      if (!videoLoaded) {
        setVideoError(true);
        setTimeout(() => setFadeIn(true), 100);
      }
    }, 5000);

    const onVisibility = () => { if (!document.hidden) playWithRetry(2, 200); };
    document.addEventListener('visibilitychange', onVisibility);
    playWithRetry();
    
    return () => {
      video.removeEventListener('error', handleVideoError);
      video.removeEventListener('canplay', handleCanPlay);
      clearTimeout(loadTimeout);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('touchstart', resumeOnUserGesture);
      window.removeEventListener('click', resumeOnUserGesture);
    };
  }, [videoLoaded]);



  return (
    <ReactLenis root>
      <div className="page contact">
        <div 
          ref={videoWrapperRef}
          className={`video-background ${videoError ? 'video-error' : ''} ${fadeIn ? 'fade-in' : ''}`}
        >
          {!videoError ? (
            <video
              ref={videoRef}
              className="background-video"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            >
              <source src={CDN_CONFIG.getHomeVideoUrl(isMobile ? 'mobile' : 'desktop')} type="video/mp4" />
            </video>
          ) : (
            <div className="video-fallback" style={{ 
              width: '100%', 
              height: '100%', 
              backgroundColor: '#000' 
            }} />
          )}
        </div>
        <div className="contact-wrapper">
          <ContactForm />
        </div>
        <Footer />
      </div>
    </ReactLenis>
  );
};

export default Transition(Contact);
